import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { SelectionToggleType } from '../../../../enums/SelectionToggleType';
import { useHeapTrack } from '../../../../hooks/UseHeapTrack';
import { useRenderPosition } from '../../../../hooks/useRenderPosition';
import { FilterItemResource } from '../../../../types/FilterTypes';
import { NumberBadge } from '../../../style/Badge';
import { DropdownOptions } from '../../../style/DropdownOptions';
import { OptionItemInput } from '../../../style/OptionItemInput';
import { HeaderOptionItemLabel, OptionItemLabel } from '../../../style/OptionItemLabel';
import { OptionItem } from '../../../style/OptionsItem';
import NoResultsFilterItem from '../no-result-filter-item/NoResultFilterItem';
import SelectionToggle from '../selection-toggle/SelectionToggle';

export type MultiSelectOptionsProps = {
  options: readonly FilterItemResource[];
  values?: readonly FilterItemResource[];
  allowSearchFilter?: boolean;
  enableCheckAll?: boolean;
  maxHeight?: number;
  searchText?: string;
  cssClass?: string;
  type: 'am-label' | 'am-dropdown';
  optionItemWidth?: number;
  onChange: (values: FilterItemResource[]) => void;
  onUpdate?: (values: FilterItemResource[]) => void;
  filterHeight?: number;
  dynamicRender?: boolean;
  selectionToggleType?: SelectionToggleType;
  hideUnselectedOption?: boolean;
  strikethroughUnselectedOption?: boolean;
  children?: ReactNode;
};

const MultiSelectOptions: FC<MultiSelectOptionsProps> = (props) => {
  const dynamicRender =
    props.dynamicRender !== null && props.dynamicRender !== undefined ? props.dynamicRender : true;
  const wrapperRef = useRef<HTMLElement>(null);
  const renderPosition = useRenderPosition(wrapperRef);
  const [filteredItems, setFilteredItems] = useState<FilterItemResource[]>([...props.options]);
  const [filteredValues, setFilteredValues] = useState<FilterItemResource[]>([
    ...(props.values || []),
  ]);
  const storeCode = localStorage.getItem('am.storeCode');

  const handleChange = (item: FilterItemResource): void => {
    if (item.key === 'isPrivateIncentiveApplied') {
      useHeapTrack('Mastermind - Notes & Insights', {
        StoreCode: storeCode,
        PrivateIncentiveSelected:
          item.key === 'isPrivateIncentiveApplied' &&
          !props.values.find((value) => item.key === value.key),
      });
    }
    if (item.key === 'isLeaseBuyout') {
      useHeapTrack('Mastermind - Notes & Insights', {
        StoreCode: storeCode,
        LeaseBuyoutSelected:
          item.key === 'isLeaseBuyout' && !props.values.find((value) => item.key === value.key),
      });
    }
    if (item.key === 'Private Incentive') {
      useHeapTrack('Market Conquest - Private Incentives In Audience Filter', {
        StoreCode: storeCode,
        PrivateIncentiveSelected:
          item.key === 'Private Incentive' && !props.values.find((value) => item.key === value.key),
      });
    }
    if (props.values.find((value) => item.key === value.key)) {
      props.onChange(props.values.filter((_item) => _item.key !== item.key));
    } else {
      props.onChange([...props.values, item]);
    }
  };

  const isChecked = (item: FilterItemResource): boolean =>
    !!props.values.find((value) => item.key === value.key);

  const toggleSelection = (): void => {
    if (filteredValues?.length > 0) {
      props.onChange(
        props.values.filter(
          (item) => !(filteredItems.findIndex((value) => item.key === value.key) > -1),
        ),
      );
    } else {
      const newValues = [...props.values, ...filteredItems];
      props.onChange(
        newValues.filter(
          (item, pos) => newValues.findIndex((value) => item.key === value.key) === pos,
        ),
      );
    }
  };

  const generateOptions = (): JSX.Element[] => {
    return filteredItems.map((item) => {
      const strikethrough = isChecked(item) === false && props.strikethroughUnselectedOption;
      return (
        <OptionItem key={item.key} onClick={() => handleChange(item)} width={props.optionItemWidth}>
          <span className='align-item'>
            <OptionItemInput
              type='checkbox'
              top={2}
              checked={isChecked(item)}
              onChange={() => handleChange(item)}
            />
            {props.type === 'am-label' && (
              <HeaderOptionItemLabel strikethrough={strikethrough}>
                {item.label}
              </HeaderOptionItemLabel>
            )}
            {props.type === 'am-dropdown' && (
              <OptionItemLabel strikethrough={strikethrough}>{item.label}</OptionItemLabel>
            )}
          </span>
          {item.count !== null && item.count !== undefined && (
            <NumberBadge>{item.count}</NumberBadge>
          )}
        </OptionItem>
      );
    });
  };

  const generateOptionWithHideOptions = (): JSX.Element[] => {
    return filteredItems.map((item) => {
      if (item.visible) {
        return (
          <OptionItem
            key={item.key}
            onClick={() => handleChange(item)}
            width={props.optionItemWidth}
          >
            <span className='align-item'>
              <OptionItemInput
                type='checkbox'
                top={2}
                checked={isChecked(item)}
                onChange={() => handleChange(item)}
              />
              {props.type === 'am-label' && (
                <HeaderOptionItemLabel>{item.label}</HeaderOptionItemLabel>
              )}
              {props.type === 'am-dropdown' && <OptionItemLabel>{item.label}</OptionItemLabel>}
            </span>
            {item.count !== null && item.count !== undefined && (
              <NumberBadge>{item.count}</NumberBadge>
            )}
          </OptionItem>
        );
      }
    });
  };

  useEffect(() => {
    const filteredOptions = [...props.options].filter((item) =>
      (item.label + '').toLocaleLowerCase().includes(props.searchText.toLocaleLowerCase()),
    );
    setFilteredItems(filteredOptions);
  }, [props.options, props.searchText]);

  useEffect(() => {
    const filteredOptions = [...props.values].filter((item) =>
      (item.label + '').toLocaleLowerCase().includes(props.searchText.toLocaleLowerCase()),
    );
    setFilteredValues(filteredOptions);
  }, [props.values, props.searchText]);

  useEffect(() => {
    wrapperRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [renderPosition]);

  return (
    <DropdownOptions
      ref={wrapperRef as any}
      renderPosition={renderPosition}
      className={`multi-select ${props.cssClass}`}
      maxHeight={280}
      dynamicRender={dynamicRender}
      filterHeight={props.filterHeight}
      searchable={props.allowSearchFilter}
    >
      {props.children}
      <div className='options unflex'>
        {filteredItems?.length === 0 && <NoResultsFilterItem />}
        {props.enableCheckAll &&
          filteredItems?.length > 0 &&
          (!props.searchText || filteredItems?.length > 0) && (
            <SelectionToggle
              indeterminate={
                filteredValues?.length > 0 && filteredValues?.length !== filteredItems?.length
              }
              selected={filteredValues?.length === filteredItems?.length}
              selectionToggleType={props.selectionToggleType}
              toggleSelection={toggleSelection}
            />
          )}
        {props.hideUnselectedOption ? generateOptionWithHideOptions() : generateOptions()}
      </div>
    </DropdownOptions>
  );
};

export default MultiSelectOptions;
